import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'
import {
  changePassword,
  getMyAccess,
  getMyOrgs,
  getSessions,
  logoutAll,
  renameMe,
  revokeSession,
} from '../api/auth'
import { listMcpKeys, rotateOwnMcpKey } from '../api/mcp-keys'
import { useDialog } from '../components/dialogs/useDialog'
import { useAuth } from '../hooks/useAuth'
import type { McpKeyListItem, MyAccessRow, MyOrgEntry, SessionInfo } from '../types/api'
import { resolveOrgSlug } from '../utils/org-slug'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-harbor-border bg-harbor-surface p-6">
      <h2 className="text-base font-semibold text-harbor-text mb-4">{title}</h2>
      {children}
    </section>
  )
}

function Loading({ label = 'Loading…' }: { label?: string }) {
  return <p className="text-sm text-harbor-text-muted">{label}</p>
}

function Empty({ label }: { label: string }) {
  return <p className="text-sm text-harbor-text-muted">{label}</p>
}

export default function ProfilePage() {
  const { user } = useAuth()
  const dialog = useDialog()

  const [orgs, setOrgs] = useState<MyOrgEntry[] | null>(null)
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null)
  const [keys, setKeys] = useState<McpKeyListItem[] | null>(null)
  const [access, setAccess] = useState<MyAccessRow[] | null>(null)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [rotationNudge, setRotationNudge] = useState(false)

  const [newUsername, setNewUsername] = useState(user?.username ?? '')
  const [isRenaming, setIsRenaming] = useState(false)

  const [revealedToken, setRevealedToken] = useState<string | null>(null)
  const [isRotatingKey, setIsRotatingKey] = useState(false)
  const [revokingSessionId, setRevokingSessionId] = useState<number | null>(null)
  const [isRevokingAll, setIsRevokingAll] = useState(false)

  const loadOrgs = useCallback(() => {
    getMyOrgs()
      .then((data) => setOrgs(data.orgs))
      .catch(() => setOrgs([]))
  }, [])

  const loadSessions = useCallback(() => {
    getSessions()
      .then((data) => setSessions(data.sessions))
      .catch(() => setSessions([]))
  }, [])

  const loadKeys = useCallback(() => {
    listMcpKeys()
      .then((data) => setKeys(data.keys))
      .catch(() => setKeys([]))
  }, [])

  const loadAccess = useCallback(() => {
    getMyAccess(20)
      .then((data) => setAccess(data))
      .catch(() => setAccess([]))
  }, [])

  useEffect(() => {
    loadOrgs()
    loadSessions()
    loadKeys()
    loadAccess()
  }, [loadOrgs, loadSessions, loadKeys, loadAccess])

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword.length < 8 || newPassword.length > 128) {
      toast.error('New password must be between 8 and 128 characters')
      return
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    setIsChangingPassword(true)
    try {
      const res = await changePassword(newPassword, currentPassword || undefined)
      setRotationNudge(res.rotationNudge)
      toast.success('Password changed. Please log in again.')
      // All sessions (including this one) are revoked server-side.
      window.location.href = '/login'
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to change password')
    } finally {
      setIsChangingPassword(false)
    }
  }

  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = newUsername.trim()
    if (!trimmed) {
      toast.error('Username is required')
      return
    }
    setIsRenaming(true)
    try {
      const res = await renameMe(trimmed)
      if (res.accessToken && res.refreshToken) {
        const slug = resolveOrgSlug() ?? 'default'
        localStorage.setItem(`accessToken:${slug}`, res.accessToken)
        localStorage.setItem(`refreshToken:${slug}`, res.refreshToken)
      }
      toast.success('Username updated')
      window.location.reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update username')
    } finally {
      setIsRenaming(false)
    }
  }

  const handleRevokeSession = async (id: number, isCurrent: boolean) => {
    if (isCurrent) {
      toast.error('Use Logout to end the current session')
      return
    }
    const confirmed = await dialog.confirm({
      title: 'Revoke session?',
      description: 'That device will be signed out immediately.',
      confirmLabel: 'Revoke session',
      tone: 'danger',
    })
    if (!confirmed) return
    setRevokingSessionId(id)
    try {
      await revokeSession(id)
      setSessions((prev) => (prev ? prev.filter((s) => s.id !== id) : prev))
      toast.success('Session revoked')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to revoke session')
    } finally {
      setRevokingSessionId(null)
    }
  }

  const handleRevokeAllOthers = async () => {
    const confirmed = await dialog.confirm({
      title: 'Revoke all other sessions?',
      description: 'Every other device will be signed out immediately.',
      confirmLabel: 'Revoke all others',
      tone: 'danger',
    })
    if (!confirmed) return
    setIsRevokingAll(true)
    try {
      await logoutAll()
      await loadSessions()
      toast.success('All other sessions revoked')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to revoke sessions')
    } finally {
      setIsRevokingAll(false)
    }
  }

  const handleRotateKey = async () => {
    const confirmed = await dialog.confirm({
      title: 'Rotate MCP API key?',
      description: 'The previous key will stop working immediately.',
      confirmLabel: 'Rotate key',
      tone: 'danger',
    })
    if (!confirmed) return
    setIsRotatingKey(true)
    try {
      const rotated = await rotateOwnMcpKey()
      setRevealedToken(rotated.token)
      await loadKeys()
      toast.success('MCP API key rotated')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to rotate MCP API key')
    } finally {
      setIsRotatingKey(false)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success('Copied to clipboard')
    } catch {
      toast.error('Failed to copy')
    }
  }

  const hasPassword = true // server accepts omitted currentPassword for OAuth-only accounts

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-harbor-text">Profile</h1>

      {/* Identity */}
      <Section title="Account">
        {user ? (
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-harbor-surface-raised flex items-center justify-center text-lg font-medium text-harbor-text">
              {user.username?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <p className="text-base font-medium text-harbor-text">{user.username}</p>
              <p className="text-sm text-harbor-text-secondary">{user.email}</p>
              <p className="text-xs text-harbor-text-muted mt-1">
                Role: {user.role}
                {' · '}
                {user.emailVerified ? 'Email verified' : 'Email not verified'}
              </p>
            </div>
          </div>
        ) : (
          <Loading />
        )}
      </Section>

      {/* Organizations (read-only) */}
      <Section title="Organizations">
        {orgs === null ? (
          <Loading />
        ) : orgs.length === 0 ? (
          <Empty label="No organizations found." />
        ) : (
          <ul className="divide-y divide-harbor-border">
            {orgs.map((o) => (
              <li key={o.slug} className="py-2 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-harbor-text">{o.name}</p>
                  <p className="text-xs font-mono text-harbor-text-muted">{o.slug}</p>
                </div>
                <span className="text-xs text-harbor-text-secondary">{o.role}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Username */}
      <Section title="Username">
        <form onSubmit={(e) => void handleRename(e)} className="space-y-3">
          <p className="text-xs text-harbor-text-muted">
            Usernames are unique within each organization. Lowercased automatically.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              minLength={2}
              maxLength={64}
              className="flex-1 px-3 py-2 text-sm rounded-lg bg-harbor-surface-raised border border-harbor-border text-harbor-text"
            />
            <button
              type="submit"
              disabled={isRenaming}
              className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {isRenaming ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Section>

      {/* Password */}
      <Section title="Password">
        {rotationNudge && (
          <p className="text-sm text-amber-400 mb-3">
            Consider rotating your MCP API key below — it was issued under your old credentials.
          </p>
        )}
        <form onSubmit={(e) => void handleChangePassword(e)} className="space-y-3">
          {hasPassword && (
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Current password (leave blank for OAuth-only accounts)"
              autoComplete="current-password"
              className="w-full px-3 py-2 text-sm rounded-lg bg-harbor-surface-raised border border-harbor-border text-harbor-text"
            />
          )}
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="New password (8–128 characters)"
            autoComplete="new-password"
            minLength={8}
            maxLength={128}
            className="w-full px-3 py-2 text-sm rounded-lg bg-harbor-surface-raised border border-harbor-border text-harbor-text"
          />
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            autoComplete="new-password"
            className="w-full px-3 py-2 text-sm rounded-lg bg-harbor-surface-raised border border-harbor-border text-harbor-text"
          />
          <p className="text-xs text-harbor-text-muted">
            Changing your password signs you out on all devices.
          </p>
          <button
            type="submit"
            disabled={isChangingPassword}
            className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {isChangingPassword ? 'Changing…' : 'Change password'}
          </button>
        </form>
      </Section>

      {/* Sessions */}
      <Section title="Sessions">
        {sessions === null ? (
          <Loading />
        ) : sessions.length === 0 ? (
          <Empty label="No active sessions." />
        ) : (
          <div className="space-y-2">
            <div className="flex justify-end">
              <button
                type="button"
                disabled={isRevokingAll}
                onClick={() => void handleRevokeAllOthers()}
                className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
              >
                {isRevokingAll ? 'Revoking…' : 'Revoke all others'}
              </button>
            </div>
            <ul className="divide-y divide-harbor-border">
              {sessions.map((s) => (
                <li key={s.id} className="py-2 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm text-harbor-text truncate">
                      {s.userAgent || 'Unknown device'}
                      {s.isCurrent && (
                        <span className="ml-2 text-xs text-emerald-400">current</span>
                      )}
                    </p>
                    <p className="text-xs text-harbor-text-muted">
                      {s.ipAddress ?? '—'}
                      {' · last used '}
                      {new Date(s.lastUsedAt).toLocaleString()}
                    </p>
                  </div>
                  {!s.isCurrent && (
                    <button
                      type="button"
                      disabled={revokingSessionId === s.id}
                      onClick={() => void handleRevokeSession(s.id, s.isCurrent)}
                      className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50 shrink-0"
                    >
                      {revokingSessionId === s.id ? 'Revoking…' : 'Revoke'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      {/* API keys */}
      <Section title="MCP API Key">
        {keys === null ? (
          <Loading />
        ) : keys.length === 0 ? (
          <Empty label="No MCP key." />
        ) : (
          <div className="space-y-3">
            {keys.map((k) => (
              <div key={k.id} className="flex items-center gap-4 text-sm flex-wrap">
                <span className="font-mono text-harbor-text">{k.tokenPrefix}…</span>
                <span className="text-harbor-text-muted text-xs">
                  {k.lastUsedAt
                    ? `Last used ${new Date(k.lastUsedAt).toLocaleString()}`
                    : 'Never used'}
                </span>
                <button
                  type="button"
                  disabled={isRotatingKey}
                  onClick={() => void handleRotateKey()}
                  className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50 text-xs"
                >
                  {isRotatingKey ? 'Rotating…' : 'Rotate'}
                </button>
              </div>
            ))}
          </div>
        )}
        {revealedToken && (
          <div className="mt-3 p-3 rounded-lg bg-harbor-surface-raised border border-harbor-border">
            <p className="text-xs text-harbor-text-muted mb-2">
              New key — shown once. Copy it now:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs font-mono text-harbor-text break-all">
                {revealedToken}
              </code>
              <button
                type="button"
                onClick={() => void copyToClipboard(revealedToken)}
                className="text-xs text-emerald-400 hover:text-emerald-300 shrink-0"
              >
                Copy
              </button>
            </div>
          </div>
        )}
        <p className="text-xs text-harbor-text-muted mt-3">
          Full setup instructions:{' '}
          <Link to="/setup/mcp" className="underline">
            MCP setup
          </Link>
        </p>
      </Section>

      {/* Recent access */}
      <Section title="Recent Access">
        {access === null ? (
          <Loading />
        ) : access.length === 0 ? (
          <Empty label="No recent connections." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="text-harbor-text-muted">
                <tr>
                  <th className="text-left pr-4 py-1">Client</th>
                  <th className="text-left pr-4 py-1">IP</th>
                  <th className="text-left pr-4 py-1">Auth</th>
                  <th className="text-left pr-4 py-1">Path</th>
                  <th className="text-left py-1">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-harbor-border">
                {access.map((row, i) => (
                  <tr key={`${row.ip}-${row.timestamp}-${i}`}>
                    <td className="pr-4 py-1 text-harbor-text">{row.client || '—'}</td>
                    <td className="pr-4 py-1 font-mono text-harbor-text-secondary">{row.ip}</td>
                    <td className="pr-4 py-1 text-harbor-text-secondary">{row.authMethod}</td>
                    <td className="pr-4 py-1 font-mono text-harbor-text-secondary">{row.path}</td>
                    <td className="py-1 text-harbor-text-muted">
                      {new Date(row.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  )
}
